import { IconBook, IconBrandLoom, IconFileText, IconHeadphones, IconLoader2, IconPhoto } from "@tabler/icons-react"
import { type ComponentType, type FC, type ReactNode } from "react"
import { Navigate, useNavigate, useParams } from "react-router"

import { type BokaderoCourse, type BokaderoIntendedPageType } from "@forento/core/models/bokadero"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import LexicalViewer from "@forento/ui/components/LexicalViewer"
import { BunnyPlayer, LoomPlayer, VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"
import { cn } from "@forento/ui/utilities/tailwind"

import { CustomIcon, InputIcon, QuizIcon, TextIcon, VideoIcon, VimeoIcon, YouTubeIcon } from "~/components/Icon"
import Layout, { PageBreadcrumb } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const BokaderoCoursePage: FC = () => {
	const bokaderoCourseId = useParams().bokaderoCourseId ?? ""

	const platform = usePlatform()!

	const { data: course, error } = query.course.bokadero.getSubmitted.useQuery(bokaderoCourseId)

	if (platform.specialPlatform !== "bokadero" || course === null) {
		return <Navigate to={`${routes.course.index()}#submitted`} replace />
	}

	return (
		<Layout>
			<PageBreadcrumb
				title={course?.name ?? "..."}
				path={[{ title: "Courses", link: `${routes.course.index()}#submitted` }]}
			/>
			{error ? (
				<p>Failed to load course.</p>
			) : course === undefined ? (
				<PartialLoadingPage />
			) : (
				<Content course={course} />
			)}
		</Layout>
	)
}

const Content: FC<{ course: Extract<BokaderoCourse, { status: "submitted" }> }> = ({ course }) => {
	const navigate = useNavigate()
	const alert = useAlert()

	const reject = query.course.bokadero.rejectCourse.useMutation({
		onSuccess: () => navigate(`${routes.course.index()}#submitted`),
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to reject course. Please try again.")
		},
	})
	const requestChange = query.course.bokadero.requestChange.useMutation({
		onSuccess: () => navigate(`${routes.course.index()}#submitted`),
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to request changes. Please try again.")
		},
	})
	const approve = query.course.bokadero.approveCourse.useMutation({
		onSuccess: () => navigate(`${routes.course.index()}#draft`),
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to approve course. Please try again.")
		},
	})

	const isSubmitting = approve.isPending || requestChange.isPending || reject.isPending

	return (
		<div className="flex flex-col gap-6 pb-26">
			<div className="fixed bottom-12 left-1/2 flex -translate-x-1/2 gap-4 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-2xl backdrop-blur-sm">
				<Button
					variant="red"
					isDisabled={isSubmitting}
					isLoading={reject.isPending}
					onClick={async () => {
						const result = await alert.input(
							"Reject course",
							"Please provide a reason for the rejection. The user will not be able to submit the course again.",
							"Reason",
						)
						if (!result.result) return
						reject.mutate({ id: course.id, reason: result.result })
						result.close()
					}}
				>
					Reject
				</Button>
				<Button
					variant="orange"
					isDisabled={isSubmitting}
					isLoading={requestChange.isPending}
					onClick={async () => {
						const result = await alert.input(
							"Request changes",
							"Please provide a description for the change request.",
							"Description",
						)
						if (!result.result) return
						requestChange.mutate({ id: course.id, reason: result.result })
						result.close()
					}}
				>
					Request changes
				</Button>
				<Button
					variant="green"
					isDisabled={isSubmitting}
					isLoading={approve.isPending}
					onClick={async () => {
						const result = await alert.confirm(
							"Approve course",
							"Are you sure you want to approve this course?",
							{ isDestructive: false },
						)
						if (!result.result) return
						approve.mutate({ id: course.id })
						result.close()
					}}
				>
					Approve
				</Button>
			</div>

			<div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow">
				<div className="flex gap-6 border-b border-gray-200 bg-linear-to-br from-gray-50 to-white p-8">
					<div className="shrink-0">
						{course.thumbnailUrl ? (
							<img
								src={course.thumbnailUrl}
								alt={course.name}
								className="aspect-video h-30 rounded-lg border border-gray-200 object-cover shadow-sm"
							/>
						) : (
							<div className="flex aspect-video h-30 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 text-3xl text-gray-400">
								<IconPhoto className="size-8" />
							</div>
						)}
					</div>
					<div className="flex flex-1 flex-col justify-center">
						<h1 className="mb-2 text-2xl leading-tight font-semibold text-gray-900">{course.name}</h1>
						<p className="leading-relaxed text-gray-600">{course.shortDescription}</p>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 bg-white p-8 sm:grid-cols-2 lg:grid-cols-4">
					<div className="flex flex-col gap-1">
						<span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
							Submitted by
						</span>
						<span className="text-base font-semibold text-gray-900">
							{course.user.firstName} {course.user.lastName}
						</span>
						<span className="text-xs text-gray-500">{course.user.email}</span>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
							Submit date
						</span>
						<span className="text-base font-semibold text-gray-900">
							{course.submitDate ? toRelativeDateString(course.submitDate) : "Unknown"}
						</span>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">Price</span>
						<span className="text-base font-semibold tracking-wider text-gray-900">
							{course.price !== null ? `${course.price.toFixed(2)} kr` : "Free"}
						</span>
					</div>
					<div className="flex flex-col gap-1">
						<span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">
							Number of pages
						</span>
						<span className="text-base font-semibold text-gray-900">
							{course.chapters?.reduce((sum, chapter) => sum + (chapter.pages?.length ?? 0), 0) ?? 0}
						</span>
					</div>
				</div>

				{course.presentationHtml && (
					<div className="px-8 pb-8">
						<div className="mt-6 mb-4 flex items-center gap-3">
							<IconFileText className="size-6 text-blue-600" />
							<h2 className="text-lg font-semibold text-gray-900">Presentation</h2>
						</div>
						<div
							className="rich-content rounded-lg border border-gray-200 bg-gray-50 p-6 leading-relaxed text-gray-900"
							dangerouslySetInnerHTML={{ __html: course.presentationHtml }}
						/>
					</div>
				)}
			</div>

			{course.chapters && course.chapters.length > 0 && (
				<div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow">
					<div className="border-b border-gray-200 bg-linear-to-br from-gray-50 to-white p-8">
						<div className="flex items-center gap-3">
							<IconBook className="size-6 text-blue-600" />
							<h2 className="text-lg font-semibold text-gray-900">Chapters & Pages</h2>
						</div>
					</div>
					<div className="p-8">
						<div className="space-y-8">
							{course.chapters.map((chapter, chapterIndex) => (
								<ChapterSection key={chapter.id} chapter={chapter} chapterIndex={chapterIndex} />
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

type ChapterSectionProps = {
	chapter: Extract<BokaderoCourse, { status: "submitted" }>["chapters"][number]
	chapterIndex: number
}
const ChapterSection: FC<ChapterSectionProps> = ({ chapter, chapterIndex }) => (
	<div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
		<div className="mb-4 flex items-center gap-3">
			<span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
				{chapterIndex + 1}
			</span>
			<h3 className="text-lg font-semibold text-gray-900">{chapter.name}</h3>
		</div>
		{chapter.pages && chapter.pages.length > 0 ? (
			<div className="space-y-4">
				{chapter.pages.map((page, pageIndex) => (
					<PageSection key={page.id} page={page} pageIndex={pageIndex} />
				))}
			</div>
		) : (
			<p className="text-sm text-gray-500 italic">No pages in this chapter</p>
		)}
	</div>
)

const pageTypeIcons: Record<BokaderoIntendedPageType, ComponentType<{ className?: string }>> = {
	video: VideoIcon,
	youtube: YouTubeIcon,
	vimeo: VimeoIcon,
	audio: IconHeadphones,
	quiz: QuizIcon,
	input: InputIcon,
	text: TextIcon,
	loom: IconBrandLoom,
	custom: CustomIcon,
}
const pageTypeLabels: Record<BokaderoIntendedPageType, string> = {
	text: "Text",
	video: "Video",
	youtube: "YouTube",
	vimeo: "Vimeo",
	audio: "Audio",
	loom: "Loom",
	quiz: "Quiz",
	input: "Input",
	custom: "Custom",
}

type Page = Extract<BokaderoCourse, { status: "submitted" }>["chapters"][number]["pages"][number]
type PageSectionProps = { page: Page; pageIndex: number }
const PageSection: FC<PageSectionProps> = ({ page, pageIndex }) => {
	const Icon = pageTypeIcons[page.intendedType]

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-4">
			<div className="mb-3 flex items-center gap-3">
				<span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
					{pageIndex + 1}
				</span>
				<div className="flex items-center gap-2">
					<Icon className="size-4 text-gray-500" />
					<span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
						{pageTypeLabels[page.intendedType]}
					</span>
				</div>
				<h4 className="text-base font-semibold text-gray-900">{page.name}</h4>
			</div>

			{page.imageUrl && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Image</h5>
					<img src={page.imageUrl} alt="Image" className="aspect-video w-full max-w-sm object-cover" />
				</div>
			)}

			{page.contentHtml && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Content</h5>
					{page.intendedType === "custom" ? (
						<div dangerouslySetInnerHTML={{ __html: page.contentHtml }} className="rich-content" />
					) : (
						<LexicalViewer html={page.contentHtml} />
					)}
				</div>
			)}

			{page.audioUrl && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Audio</h5>
					<audio controls src={page.audioUrl} className="w-full max-w-sm" />
				</div>
			)}

			{(page.bunnyVideoId || page.youTubeVideoId || page.vimeoVideoId || page.loomVideoId) && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Video</h5>
					<div className="max-w-sm">
						{page.bunnyVideoId ? (
							<BunnyPlayer videoId={page.bunnyVideoId} />
						) : page.youTubeVideoId ? (
							<YoutubePlayer videoId={page.youTubeVideoId} />
						) : page.vimeoVideoId ? (
							<VimeoPlayer videoId={page.vimeoVideoId} />
						) : page.loomVideoId ? (
							<LoomPlayer videoId={page.loomVideoId} />
						) : null}
					</div>
				</div>
			)}

			{page.intendedType === "input" && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Inmatning</h5>
					<div className="text-sm text-gray-600">
						{page.inputType === "single-line" ? "Single line" : "Multiple lines"}
					</div>
				</div>
			)}

			{page.answers.length > 0 && (
				<div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
					<div className="mb-3 flex items-center gap-2">
						<span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-600 shadow">
							📝
						</span>
						<h5 className="text-base font-semibold text-green-800">Quiz Answers</h5>
					</div>
					<ul className="space-y-2">
						{page.answers.map(answer => (
							<li
								key={answer.id}
								className={`flex items-center gap-3 rounded-md px-3 py-2 ${
									answer.isCorrect
										? "border border-green-200 bg-green-100"
										: "border border-gray-200 bg-white"
								}`}
							>
								<span
									className={`flex h-5 w-5 items-center justify-center rounded-full border ${
										answer.isCorrect
											? "border-green-500 bg-green-500 text-white"
											: "border-gray-300 bg-gray-100 text-gray-400"
									} text-xs font-bold`}
								>
									{answer.isCorrect ? "✓" : "○"}
								</span>
								<span
									className={`text-sm ${
										answer.isCorrect ? "font-semibold text-green-700" : "text-gray-700"
									}`}
								>
									{answer.text}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}

			{page.attachments.length > 0 && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Attachments</h5>
					<div className="space-y-1">
						{page.attachments.map(attachment => (
							<div key={attachment.id} className="text-sm text-gray-600">
								{attachment.label}
							</div>
						))}
					</div>
				</div>
			)}

			{page.links.length > 0 && (
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Links</h5>
					<div className="space-y-1">
						{page.links.map(link => (
							<div key={link.id} className="text-sm text-gray-600">
								<a href={link.url} target="_blank" rel="noopener noreferrer">
									<span className="font-medium">{link.label}</span> - {link.url}
								</a>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

type ButtonProps = {
	variant: "red" | "orange" | "green"
	isLoading: boolean
	isDisabled: boolean
	onClick(): void
	children: ReactNode
}
const Button: FC<ButtonProps> = ({ variant, isLoading, isDisabled, onClick, children }) => {
	return (
		<button
			type="button"
			className={cn(
				"flex cursor-pointer items-center gap-2 rounded-lg px-6 py-2.5 font-medium text-white transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none",
				variant === "red"
					? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
					: variant === "orange"
						? "bg-orange-500 hover:bg-orange-600 focus:ring-orange-500"
						: variant === "green"
							? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
							: exhaustiveGuard(variant),
				(isLoading || isDisabled) && "cursor-not-allowed opacity-50",
			)}
			disabled={isDisabled || isLoading}
			onClick={onClick}
		>
			{isLoading && <IconLoader2 className="size-4 animate-spin" />}
			{children}
		</button>
	)
}

export default BokaderoCoursePage
